/**
 * Module dependencies.
 */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Export `useQueryState`.
 *
 * A piece of UI state (typically a table filter) mirrored to the URL query
 * string, so it survives a refresh and is shareable. Reads `defaultValue` when
 * the param is absent, and drops the param when set back to the default to keep
 * URLs clean. History is replaced (not pushed) so filtering doesn't spam Back.
 */

export function useQueryState(key: string, defaultValue = ''): [string, (value: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? defaultValue;

  const setValue = useCallback(
    (next: string) => {
      setParams(
        previous => {
          const searchParams = new URLSearchParams(previous);

          if (!next || next === defaultValue) {
            searchParams.delete(key);
          } else {
            searchParams.set(key, next);
          }

          return searchParams;
        },
        { replace: true }
      );
    },
    [key, defaultValue, setParams]
  );

  return [value, setValue];
}
